'use client';

import { tracking } from '@/lib/features/tracking';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { PLANNER_MODE_MESSAGES, ROLE_TYPES } from '../../constants';
import { useStudioAI } from '../../context';
import type { ChatMessage, PlannerFormState } from '../../types';
import { generateConversationId, looksLikePlannerJson, parseAllPlannerCards } from '../../utils';
import { Markdown } from '../markdown';
import { StatusMessage } from '../status-message';
import { PlannerCard } from './planner-card';
import type { PlannerPlan } from './types';

interface PlannerMessageProps {
    message: ChatMessage;
    threadId: string | null;
    messages: ChatMessage[];
    msgIndex: number;
    plannerFormState: PlannerFormState | null;
}

interface ExtractedStepState {
    selectedOptionId?: string;
    isFieldSubmitted?: boolean;
    isModuleSubmitted?: boolean;
    formState?: PlannerFormState;
}

/**
 * Recovers step-card interaction state from user messages following the
 * assistant message so that reloads restore the correct UI state.
 * Matches by card_index when present; legacy messages without card_index
 * are treated as belonging to card 0 for backwards compatibility.
 */
function extractStepFormState(
    messages: ChatMessage[],
    afterIndex: number,
    cardIndex: number,
): ExtractedStepState | null {
    const userMsgsAfter = messages.slice(afterIndex + 1).filter((m) => m.role === ROLE_TYPES.USER);

    for (const userMsg of userMsgsAfter) {
        const rawContent =
            typeof userMsg.content === 'string'
                ? userMsg.content
                : userMsg.content?.find((c) => c.type === 'text')?.text;
        if (!rawContent) continue;

        try {
            const parsed = JSON.parse(rawContent);
            if (!parsed?.action) continue;

            const parsedCardIndex = parsed.card_index ?? 0;
            if (parsedCardIndex !== cardIndex) continue;

            switch (parsed.action) {
                case 'planner_step_response':
                    return { selectedOptionId: parsed.option_id };
                case 'planner_step_fields':
                    return {
                        isFieldSubmitted: true,
                        formState: { submitted: true, ...parsed.fields },
                    };
                case 'planner_step_modules':
                    return {
                        isModuleSubmitted: true,
                        formState: {
                            submitted: true,
                            enabled_modules: parsed.enabled_modules,
                        },
                    };
                default:
                    continue;
            }
        } catch {
            continue;
        }
    }
    return null;
}

export function PlannerMessage({
    message,
    threadId,
    messages,
    msgIndex,
    plannerFormState,
}: PlannerMessageProps) {
    const router = useRouter();
    const {
        currentConversationId,
        currentThreadId,
        setCurrentConversationId,
        addMessage,
        plannerRunId,
        sendSilentMessage,
        agentMode,
        handleReportKickedOff,
        navigationDisabled,
    } = useStudioAI();

    const [loadingPathId, setLoadingPathId] = useState<string | null>(null);
    const [selectedStepOptionIds, setSelectedStepOptionIds] = useState<Record<number, string>>({});
    const [fieldSubmittedCards, setFieldSubmittedCards] = useState<Set<number>>(new Set());
    const [moduleSubmittedCards, setModuleSubmittedCards] = useState<Set<number>>(new Set());

    const handleSubmit = useCallback(
        async (payloadStr: string, pathId: string) => {
            try {
                setLoadingPathId(pathId);
                const parsed = JSON.parse(payloadStr);
                const conversationId = currentConversationId || generateConversationId();
                const runId = parsed.run_id as string | undefined;

                setCurrentConversationId(conversationId);

                if (runId) {
                    const brandLabel =
                        (typeof parsed.brand === 'string' && parsed.brand) ||
                        (typeof parsed.brand_name === 'string' && parsed.brand_name) ||
                        undefined;
                    handleReportKickedOff(runId, brandLabel || undefined);
                }
                if (!navigationDisabled) {
                    router.replace(`/dani/${conversationId}`, { scroll: false });
                }

                // Build the plannerFormState snapshot the server should
                // persist onto studioai_history so the planner form renders
                // locked after a refresh.
                const plannerFormState: Record<string, unknown> = {
                    action: parsed.action,
                    brand: parsed.brand ?? parsed.brand_name ?? '',
                    competitors: parsed.competitors ?? [],
                    verticals: parsed.verticals ?? [],
                    enabled_modules: parsed.enabled_modules ?? [],
                    time_period: parsed.time_period ?? 'last_90_days',
                    depth: parsed.depth,
                    submitted: true,
                };
                if (parsed.templateId != null) {
                    plannerFormState.templateId = parsed.templateId;
                }

                await sendSilentMessage({
                    prompt: 'Run report',
                    conversationId,
                    parentMessageId: '0',
                    agentMode,
                    threadId: currentThreadId ?? undefined,
                    contextData: {
                        runId,
                        action: 'run_report',
                        plannerFormState,
                    },
                });

                setLoadingPathId(null);
            } catch (err) {
                tracking.errorWithSource(
                    err instanceof Error ? err : new Error(String(err)),
                    'PlannerMessage.handleSubmit',
                );
                addMessage({
                    id: `report_error_${Date.now()}`,
                    role: 'assistant',
                    content: [
                        {
                            type: 'text',
                            text: 'Something went wrong while submitting your report. Please try again.',
                        },
                    ],
                    isStreaming: false,
                    timestamp: new Date(),
                });
                setLoadingPathId(null);
            }
        },
        [
            currentConversationId,
            currentThreadId,
            setCurrentConversationId,
            addMessage,
            router,
            handleReportKickedOff,
            sendSilentMessage,
            agentMode,
            navigationDisabled,
        ],
    );

    const handleOptionSelect = useCallback(
        async (optionId: string, optionLabel: string, cardIndex: number) => {
            setSelectedStepOptionIds((prev) => ({ ...prev, [cardIndex]: optionId }));
            const conversationId = currentConversationId || generateConversationId();
            try {
                const payload = JSON.stringify({
                    action: 'planner_step_response',
                    option_id: optionId,
                    option_label: optionLabel,
                    card_index: cardIndex,
                });
                await sendSilentMessage({
                    prompt: payload,
                    conversationId,
                    parentMessageId: '0',
                    agentMode,
                });
            } catch {
                setSelectedStepOptionIds((prev) => {
                    const next = { ...prev };
                    delete next[cardIndex];
                    return next;
                });
            }
        },
        [currentConversationId, sendSilentMessage, agentMode],
    );

    const handleFieldSubmit = useCallback(
        async (fieldValues: Record<string, string | string[]>, cardIndex: number) => {
            setFieldSubmittedCards((prev) => new Set(prev).add(cardIndex));
            const conversationId = currentConversationId || generateConversationId();
            try {
                const payload = JSON.stringify({
                    action: 'planner_step_fields',
                    fields: fieldValues,
                    card_index: cardIndex,
                });
                await sendSilentMessage({
                    prompt: payload,
                    conversationId,
                    parentMessageId: '0',
                    agentMode,
                });
            } catch {
                setFieldSubmittedCards((prev) => {
                    const next = new Set(prev);
                    next.delete(cardIndex);
                    return next;
                });
            }
        },
        [currentConversationId, sendSilentMessage, agentMode],
    );

    const handleModuleSubmit = useCallback(
        async (enabledModuleIds: string[], cardIndex: number) => {
            setModuleSubmittedCards((prev) => new Set(prev).add(cardIndex));
            const conversationId = currentConversationId || generateConversationId();
            try {
                const payload = JSON.stringify({
                    action: 'planner_step_modules',
                    enabled_modules: enabledModuleIds,
                    card_index: cardIndex,
                });
                await sendSilentMessage({
                    prompt: payload,
                    conversationId,
                    parentMessageId: '0',
                    agentMode,
                });
            } catch {
                setModuleSubmittedCards((prev) => {
                    const next = new Set(prev);
                    next.delete(cardIndex);
                    return next;
                });
            }
        },
        [currentConversationId, sendSilentMessage, agentMode],
    );

    const makeOptionSelectHandler = useCallback(
        (plan: PlannerPlan, cardIndex: number) => {
            return async (optionId: string, optionLabel: string) => {
                if (plan.run_id) {
                    const payload = JSON.stringify({
                        action: 'run_report',
                        run_id: plan.run_id,
                        depth: optionId,
                    });
                    await handleSubmit(payload, optionId);
                } else {
                    await handleOptionSelect(optionId, optionLabel, cardIndex);
                }
            };
        },
        [handleSubmit, handleOptionSelect],
    );

    if (message.isStreaming && looksLikePlannerJson(message.content)) {
        return (
            <motion.div
                key={`${threadId}-${message.id}`}
                className="group/message w-full px-4 py-3"
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
            >
                <div className="flex w-full items-start gap-4">
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                        <div className="flex max-w-full flex-col gap-4 overflow-x-auto py-3 text-foreground">
                            <StatusMessage
                                message={PLANNER_MODE_MESSAGES.BUILDING_PLAN}
                                isInternal={false}
                            />
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    const allCards = parseAllPlannerCards(message.content, message.metadata);
    if (allCards.length === 0) return null;

    const hasAnyUserAfter = messages.slice(msgIndex + 1).some((m) => m.role === ROLE_TYPES.USER);
    const hasNonSilentUserAfter = messages
        .slice(msgIndex + 1)
        .some((m) => m.role === ROLE_TYPES.USER && !m.silent);
    const isSubmitted = !!plannerFormState?.submitted;

    return (
        <motion.div
            key={`${threadId}-${message.id}`}
            className="group/message w-full px-4 py-3"
            initial={{ y: 5, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            data-role="assistant"
        >
            <div className="flex w-full items-start gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {allCards.map((cardResult, cardIndex) => {
                        const isStepCard = !!cardResult.plan.stepMode;
                        const extractedState =
                            isStepCard && hasAnyUserAfter
                                ? extractStepFormState(messages, msgIndex, cardIndex)
                                : null;
                        const cardIsReadOnly = isStepCard
                            ? hasAnyUserAfter || !!plannerRunId
                            : hasNonSilentUserAfter || isSubmitted || !!plannerRunId;

                        return (
                            <div
                                key={cardIndex}
                                className={`flex flex-col gap-4 overflow-x-auto py-3 text-foreground ${isStepCard ? 'w-fit max-w-full' : 'max-w-full'}`}
                            >
                                {cardResult.text && (
                                    <div className="text-mini text-foreground">
                                        <Markdown>{cardResult.text}</Markdown>
                                    </div>
                                )}
                                <PlannerCard
                                    plan={cardResult.plan}
                                    onSubmit={handleSubmit}
                                    isReadOnly={cardIsReadOnly}
                                    loadingPathId={loadingPathId}
                                    initialFormState={
                                        isStepCard
                                            ? (extractedState?.formState ?? null)
                                            : plannerFormState
                                    }
                                    onOptionSelect={
                                        isStepCard
                                            ? makeOptionSelectHandler(cardResult.plan, cardIndex)
                                            : undefined
                                    }
                                    onFieldSubmit={
                                        isStepCard
                                            ? (v) => handleFieldSubmit(v, cardIndex)
                                            : undefined
                                    }
                                    onModuleSubmit={
                                        isStepCard
                                            ? (ids) => handleModuleSubmit(ids, cardIndex)
                                            : undefined
                                    }
                                    selectedOptionId={
                                        isStepCard
                                            ? (extractedState?.selectedOptionId ??
                                              selectedStepOptionIds[cardIndex] ??
                                              null)
                                            : undefined
                                    }
                                    isFieldSubmitted={
                                        isStepCard
                                            ? extractedState?.isFieldSubmitted ||
                                              fieldSubmittedCards.has(cardIndex) ||
                                              hasAnyUserAfter
                                            : undefined
                                    }
                                    isModuleSubmitted={
                                        isStepCard
                                            ? extractedState?.isModuleSubmitted ||
                                              moduleSubmittedCards.has(cardIndex) ||
                                              hasAnyUserAfter
                                            : undefined
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </motion.div>
    );
}
